import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PLAN_MONTHLY_CREDITS,
  TRIAL_SIGNUP_CREDITS,
  monthlyCreditsLine,
  signupCreditsLine,
  planTermTotalLine,
} from '../../constants/plans';

const PricingSection = () => {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  const plans = [
    {
      name: 'Trial',
      tier: 'trial',
      // 2026-10-03 (Portone-KGinisys-Pricing-fix-10-2.md T1.2): this line named a
      // QUANTITY ("5 credits, ...") in English inside the Korean copy, with a
      // hand-typed 5. It now names the audience, like the other three cards;
      // the quantity is stated once, by the signupCreditsLine bullet below.
      targetKo: '무료 체험 · 처음 사용하는 브랜드',
      targetEn: 'Free trial · first-time users',
      price: '₩0',
      priceNote: null,
      // ⚠ '월 검증 건수 5건' / '5 validations per month' was FALSE, and it is
      // the same defect as Starter's '무제한' below, only smaller. A trial org is
      // seeded ONCE at signup (api/auth.py: organizations.credits_remaining = 5,
      // sized by config.default_trial_credits); there is no Trial plan SKU, so
      // monthly_credits_for_tier('trial') returns None and no anniversary grant
      // ever runs. The card promised that 5 again every month, forever.
      // The quantity now comes from constants/plans and says 1회 out loud.
      featuresKo: ['한국 · 미국 FDA 규제 검증', signupCreditsLine(TRIAL_SIGNUP_CREDITS, true), 'AI 대안 제안'],
      featuresEn: ['Korean and US FDA regulatory validation', signupCreditsLine(TRIAL_SIGNUP_CREDITS, false), 'AI alternatives'],
      ctaKo: '무료 시작',
      ctaEn: 'Start Free',
      ctaClass: 'btn-secondary'
    },
    {
      name: isKo ? '스타터 연간플랜' : 'Starter Yearly Plan',
      tier: 'starter',
      targetKo: '1-3인 팀, 소규모 브랜드',
      targetEn: '1-3 users, small brands',
      // 2026-09-09: the LANDING page shows the monthly figure only. The annual
      // total is presented at checkout, where the buyer is actually committing.
      //
      // ⚠ The period label stays. A4 logged a BARE '₩199,000' here as a defect,
      // because a naked monthly number on a product billed ₩2,388,000 up front
      // reads as a monthly subscription — the same class of problem as the
      // ₩10,000 Pro price that /pricing advertised. Dropping the annual TOTAL is
      // a presentation choice; dropping the fact that it is billed annually is a
      // misrepresentation, so only the former is done here.
      //
      // D-2 (2026-10-03): the Korean note said "연간 선불 결제". PortOne lists
      // 선불 서비스 as a high-risk category of its own, so it now says how the
      // fee is charged. English keeps "billed annually". Never "일시불", which at
      // card checkout means "no installments".
      price: '₩199,000',
      priceNote: isKo ? '월 환산 · 12개월 이용료 일괄 결제' : 'per month · billed annually',
      // ⚠⚠ THE TWO '무제한' BULLETS WERE FALSE AND ARE GONE. Starter is capped at
      // 200 credits a month (product_catalogue.plan_starter_12m), every analysis
      // spends exactly one (analyze.py ANALYSIS_CREDIT_COST), and the analyse
      // button is hard-blocked at a balance of 0 (App.js). '월 검증 건수 무제한'
      // was therefore an advertised entitlement we do not deliver -- the shape
      // the 2-credits incident took (.claude/TODO/Credit-manage-analysis-9-23.md
      // §10: shipping less than the advertised amount is "a mis-sale, not a
      // fraud control"). Printing the real 200 in the price block while this
      // bullet still said 무제한 would have put both claims on one card.
      //
      // 'FDA 성분 검증 무제한' went with it, and NOT because FDA checking is
      // unreal -- it is real and it stays, as a CAPABILITY. There is no separate
      // free FDA ingredient lookup anywhere in the product: FDA validation is
      // delivered by the same /analyze call that spends the credit, and the only
      // ingredient catalogue endpoint (api/data_explorer.py) is behind
      // require_site_admin. So the capability is true and the quantity was not.
      // Wording matches /pricing's Trial bullets, which Starter inherits there.
      //
      // The allowance itself is not lost from the card -- it moved to the price
      // block, which is where the owner asked for it and where a buyer compares.
      featuresKo: ['한국 · 미국 FDA 규제 검증', 'AI 대안 제안', '규제 가이드 업데이트'],
      featuresEn: ['Korean and US FDA regulatory validation', 'AI alternatives', 'Regulatory guide updates'],
      ctaKo: '시작하기',
      ctaEn: 'Get Started',
      ctaClass: 'btn-secondary'
    },
    {
      // 'Team' was renamed to 'Pro' by migration 039 and never updated here.
      // That also broke the CTA below, which built ?tier= from this display name.
      name: isKo ? '프로 연간플랜' : 'Pro Yearly Plan',
      tier: 'pro',
      targetKo: '성장 브랜드, 정기 수출',
      targetEn: 'Growing brands, recurring exports',
      // Monthly only, and the same D-2 note — see the Starter notes above.
      price: '₩499,000',
      priceNote: isKo ? '월 환산 · 12개월 이용료 일괄 결제' : 'per month · billed annually',
      // Pro's bullets are unchanged and are now correct BECAUSE Starter's were
      // fixed: 'Starter 모든 기능' inherits whatever that card claims, so Pro was
      // silently advertising 무제한 검증 too while being capped at 600/month.
      // Nothing here duplicates a Starter bullet, so the inheritance reads clean.
      featuresKo: ['Starter 모든 기능', '검증 이력 관리', '감사 리포트 다운로드'],
      featuresEn: ['Everything in Starter', 'Validation history', 'Audit report download'],
      ctaKo: '시작하기',
      ctaEn: 'Get Started',
      ctaClass: 'btn-primary',
      popular: true
    },
    {
      name: 'Enterprise',
      tier: 'enterprise',
      priceNote: null,
      targetKo: 'OEM/ODM 대형 파트너',
      targetEn: 'Large OEM/ODM partners',
      price: isKo ? '문의' : 'Contact',
      featuresKo: ['무제한 검증 및 조직 운영', 'OEM/ODM 전용 검토', 'E&O 책임보험 포함', '전담 컨설턴트 배정', 'API 액세스'],
      featuresEn: ['Unlimited validation and team usage', 'OEM/ODM review workflow', 'E&O coverage included', 'Dedicated consultant', 'API access'],
      ctaKo: '영업팀 문의',
      ctaEn: 'Contact Sales',
      ctaClass: 'btn-secondary'
    }
  ];


  return (
    <section id="pricing" className="section-soft" style={{ padding: '72px 0' }}>
      <div className="wrap">
        <div className="section-head">
          <h2>Flexible Pricing for Every Brand Size</h2>
          <p>{isKo ? '브랜드 규모에 맞는 플랜을 선택하세요.' : 'Choose the plan that fits your brand.'}</p>
        </div>

        <div className="grid-pricing">
          {plans.map((plan, i) => (
            <div key={i} className={`card pricing-card${plan.popular ? ' popular' : ''}`}>
              {plan.popular && <span className="popular-badge">Most Popular</span>}
              <div className="plan-name">{plan.name}</div>
              <div className="plan-target">{isKo ? plan.targetKo : plan.targetEn}</div>
              <p className="price">{plan.price}</p>
              {/* THE INCLUDED ALLOWANCE, DIRECTLY UNDER THE PRICE.
                  Requested 2026-09-28: the card quoted a price and a tier and
                  never said what the buyer actually receives, so the one number
                  that decides whether ₩199,000/월 is worth it was missing from
                  the surface most buyers read.

                  Rendered from PLAN_MONTHLY_CREDITS, which the backend test
                  test_plan_credits_match_frontend.py pins to the catalogue that
                  performs the grant. A tier with no plan SKU (Trial, Enterprise)
                  has no key and renders NOTHING here -- we do not have an
                  allowance to state for them, and a 0 would be a claim.

                  Part of the price block, not a fifth feature: --lp-text and 700
                  put it between the price above and the muted period note below,
                  so it reads as "what ₩199,000 buys" rather than as a perk. No
                  white-space: nowrap (unlike .price) -- the EN string is long and
                  must wrap inside the card instead of widening it at phone
                  width.

                  2026-10-03 (Portone-KGinisys-Pricing-fix-10-2.md T1.2): worded
                  as a service with a fixed quantity, "매월 분석 리포트 N건
                  제공", and followed by the 12-month total KG이니시스 asked to
                  see. The total is muted and ALWAYS directly under the monthly
                  line, never on its own: the monthly grant is a hard reset, so
                  "총 2,400건" alone would read as a pool usable at once. It
                  carries the no-carry-over clause for the same reason. Same
                  no-nowrap rule: the EN total must wrap at phone width. */}
              {PLAN_MONTHLY_CREDITS[plan.tier] != null && (
                <>
                  <p
                    className="plan-credits"
                    style={{
                      margin: '0 0 4px',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: 1.4,
                      color: 'var(--lp-text)',
                    }}
                  >
                    {monthlyCreditsLine(PLAN_MONTHLY_CREDITS[plan.tier], isKo)}
                  </p>
                  <p
                    className="plan-term-total"
                    style={{
                      margin: '0 0 4px',
                      fontSize: '12px',
                      lineHeight: 1.4,
                      color: 'var(--lp-muted)',
                    }}
                  >
                    {planTermTotalLine(PLAN_MONTHLY_CREDITS[plan.tier], isKo)}
                  </p>
                </>
              )}
              {plan.priceNote && <p className="price-note">{plan.priceNote}</p>}
              <div className="feature-list">
                {(isKo ? plan.featuresKo : plan.featuresEn).map((f, j) => (
                  <div key={j} className="feature-item">
                    <span className="check">✓</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <a
                className={`btn ${plan.ctaClass}`}
                /* Uses plan.tier, NOT plan.name.toLowerCase(). The display name
                   is now localised ('프로 연간플랜'), and it was already wrong
                   before that: 'Team' produced ?tier=team, a tier renamed to
                   'pro' by migration 039. Deriving a behavioural value from a
                   display string is the same defect as the '/월' suffix. */
                href={
                  plan.tier === 'enterprise'
                    ? 'mailto:hello@whereat.kr'
                    : `/login?signup=true&tier=${plan.tier}`
                }
              >
                {isKo ? plan.ctaKo : plan.ctaEn}
              </a>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default PricingSection;
